import {IsbnModel, IsbnNotValidatedPair} from '../models/isbn.model';
import {forEach} from '@angular/router/src/utils/collection';
import {NotificationService} from '../../shared/utils/notification.service';
import {recognize} from '@angular/router/src/recognize';

export class IsbnUtils {

    static calcIsbnSum(cleanIsbn:string):number {
        let sum = 0;
        for (let i = 0; i < 12; i++) {
            sum += +cleanIsbn[i] * (i % 2 === 1 ? 3 : 1);
        }
        return((10 - sum % 10) % 10);
    }

    static isCleanIsbnValid(cleanIsbn: string): boolean {
        const cleanIsbnPatt = new RegExp('^[0-9]{13}$');
        if (!cleanIsbnPatt.test(cleanIsbn)) {
            return false
        }
        const controlNumber = this.calcIsbnSum(cleanIsbn);
        return controlNumber === +cleanIsbn[12];
    }

    static isDirtyIsbnValid(dirtyIsbn: string): boolean {
        const patt = new RegExp('^[0-9]+[-]([-]?[0-9]+)+$');
        if (patt.test(dirtyIsbn)) {
            const cleanIsbn = dirtyIsbn.split('-').join('');
            if (cleanIsbn.length === 13) {
                return this.isCleanIsbnValid(cleanIsbn);
            }
        }
        return false;
    }

    static isIsbnPairValid(cleanIsbn: string, dirtyIsbn: string): boolean {
        return dirtyIsbn.split('-').join('') === cleanIsbn;
    }


    static parseIsbn(result: any): IsbnModel[] {
        const isbnValidPairs: IsbnModel[] = [];
        const isbnNotValidatedPairs: IsbnNotValidatedPair[] = [];
        const isbnWronglyMatchedPairs: IsbnNotValidatedPair[] = [];
        let resultLines: any[] = result.lines;

        resultLines= this.cleanLines(resultLines);
        // separate line to isbn pairs
        resultLines.forEach(line => {
            this.parsePairsFromLines(isbnNotValidatedPairs, line);
        });

        // try validate all pairs, All not valid pairs put to isbnWronglyMatchedPairs
        for (const isbnNotValidPair of isbnNotValidatedPairs) {
            if (!this.validPairWithChoice(isbnNotValidPair, isbnValidPairs)) {
                isbnWronglyMatchedPairs.push(isbnNotValidPair);
            }
        }


        // try fix wrong matched pairs
        isbnWronglyMatchedPairs.forEach(isbnWronglyMatchedPair => {
            this.fixWrongMatchedPairs(isbnWronglyMatchedPair, isbnValidPairs);
        });
        return isbnValidPairs;
    }

    private static fixWrongMatchedPairs(isbnWronglyMatchedPair: IsbnNotValidatedPair, isbnValidPairs: IsbnModel[]) {
        function rebuildCleanToDirty(cleanIsbn: string):string {
            return cleanIsbn.slice(0,3)+'-'+cleanIsbn.slice(3,5)+'-'+cleanIsbn.slice(5,8)+'-'+cleanIsbn.slice(8,12)+'-'+cleanIsbn.slice(12);
        }

        function isGreaterIsbnExist(cleanIsbn:string):boolean {
            let bigggerIsbn = cleanIsbn.slice(0,12);
            bigggerIsbn = bigggerIsbn.slice(0,11)+(parseInt(bigggerIsbn[11],10)+1).toString();
            bigggerIsbn = bigggerIsbn + IsbnUtils.calcIsbnSum(bigggerIsbn);
           return(isbnValidPairs.filter(element => element.cleanNumber===bigggerIsbn).length>0);

        }
        function isLesserIsbnExist(cleanIsbn:string):boolean {
            let bigggerIsbn = cleanIsbn.slice(0,12);
            bigggerIsbn = bigggerIsbn.slice(0,11)+(parseInt(bigggerIsbn[11],10)-1).toString();
            bigggerIsbn = bigggerIsbn + IsbnUtils.calcIsbnSum(bigggerIsbn);
           return(isbnValidPairs.filter(element => element.cleanNumber===bigggerIsbn).length>0);

        }

        let probabilityOfCorrectly =0; // probality must be >=1
        let dirtyValidIsbn = this.findValidDirtyIsbnInChoices(isbnWronglyMatchedPair.dirtyChoices);
        let cleanValidIsbn = this.findValidCleanIsbnInChoices(isbnWronglyMatchedPair.cleanChoices);

        // clean is not empty ==> dirty must be empty
        if (!(cleanValidIsbn === 'empty')) {
            probabilityOfCorrectly+=0.5; // clean isbn is valid
            for (const choice of isbnWronglyMatchedPair.dirtyChoices) {
                const cleanIsbn=choice.text.split('-').join('');
                if (this.isCleanIsbnValid(cleanIsbn)) {
                    probabilityOfCorrectly+=0.5; // missing only '-'
                    break;
                }
            }
            // try find adjoining validated isbn
            if(isGreaterIsbnExist(cleanValidIsbn)) {
                probabilityOfCorrectly+=0.5 // adjoining isbn exist
            }
            if(isLesserIsbnExist(cleanValidIsbn)) {
                probabilityOfCorrectly+=0.5 // adjoining isbn exist
            }

            dirtyValidIsbn = rebuildCleanToDirty(cleanValidIsbn);
        } else {
            if(!(dirtyValidIsbn === 'empty')) {
                probabilityOfCorrectly+=0.5; // dirty isbn is valid
                cleanValidIsbn=dirtyValidIsbn.split('-').join('');
                // try find adjoining validated isbn
                if(isGreaterIsbnExist(cleanValidIsbn)) {
                    probabilityOfCorrectly+=0.5 // adjoining isbn exist
                }
                if(isLesserIsbnExist(cleanValidIsbn)) {
                    probabilityOfCorrectly+=0.5 // adjoining isbn exist
                }
            } else {
                alert('Can\'t recognize this number: '+dirtyValidIsbn);
            }
        }
        if(probabilityOfCorrectly>=1&&this.isIsbnPairValid(cleanValidIsbn,dirtyValidIsbn)) {
            const newIsbnValidPair: IsbnModel = new IsbnModel();
            newIsbnValidPair.cleanNumber = cleanValidIsbn;
            newIsbnValidPair.dirtyNumber = dirtyValidIsbn;
            isbnValidPairs.push(newIsbnValidPair)
        } else {
            alert('Can\'t recognize this number: '+dirtyValidIsbn +' ' + cleanValidIsbn);
        }
    }
    private static repairCommonDefect(text: string): string {
        text = text.split('O').join('0');
        text = text.split('*').join('-');
        text = text.split('~').join('-');
        text = text.split('_').join('-');
        text = text.split('—').join('-');
        text = text.replace(new RegExp('[a-zA-Z]', 'g'), '');
        text = text.replace(/[^0-9\-]+/g, '-');
        return text;

    }

    // remove short words
    private static cleanLines(lines:any[]) {
        for (const line of lines) {
            for (const word of line.words) {
                word.choices = word.choices.filter(choice => choice.text.length>10);
            }
        }
        for (const line of lines) {
            line.words=line.words.filter(word => word.choices.length>0);
        }
        return lines;
    }
    // return true if pair is valid and add pair to isbnValidPairs other else return false
    private static validPairWithChoice(isbnNotValidatedPair: IsbnNotValidatedPair, isbnValidPairs: IsbnModel[]): boolean {
        const dirtyValidIsbn = this.findValidDirtyIsbnInChoices(isbnNotValidatedPair.dirtyChoices);
        const cleanValidIsbn = this.findValidCleanIsbnInChoices(isbnNotValidatedPair.cleanChoices);

        if (!(dirtyValidIsbn === 'empty') || !(cleanValidIsbn === 'empty')) {
            if (this.isIsbnPairValid(cleanValidIsbn, dirtyValidIsbn)) {
                const newIsbnValidPair: IsbnModel = new IsbnModel();
                newIsbnValidPair.cleanNumber = cleanValidIsbn;
                newIsbnValidPair.dirtyNumber = dirtyValidIsbn;
                isbnValidPairs.push(newIsbnValidPair)
                return true
            }
        }
        return false
    }

    // find valid dirty isbn or return 'empty'
    private static findValidDirtyIsbnInChoices(dirtyChoices: any): string {
        let dirtyValidIsbn = 'empty';
        dirtyChoices.forEach(choice => {
            const repairedChoice = this.repairCommonDefect(choice.text);
            if (this.isDirtyIsbnValid(repairedChoice)) {
                dirtyValidIsbn = (repairedChoice);
            }
        });
        return dirtyValidIsbn

    }// find valid dirty isbn or return 'empty'
    // find valid clean isbn or return 'empty'
    private static findValidCleanIsbnInChoices(cleanChoices: any): string {
        let cleanValidIsbn = 'empty';
        cleanChoices.forEach(choice => {
            const repairedChoice = this.repairCommonDefect(choice.text);
            if (this.isCleanIsbnValid(repairedChoice)) {
                cleanValidIsbn = (repairedChoice);
            }
        });
        return cleanValidIsbn

    }

    private static parsePairsFromLines(isbnNotValidatedPairs: IsbnNotValidatedPair[], line: any) {
        function isFirstLine(element, index, array) {
            return (element.text === 'ISBN');
        }

        // catch single isbn on line
        if (line.words.length === 2) {
            const isbnPair: IsbnNotValidatedPair = new IsbnNotValidatedPair()
            isbnPair.dirtyChoices = line.words[0].choices;
            isbnPair.cleanChoices = line.words[1].choices;
            isbnNotValidatedPairs.push(isbnPair)
            return  ;
        }
        // catch two isbns on line
        if (line.words.length === 4) {
            // filter first line  ( "ISBN EAN ISBN EAN" )
            if (line.words[0].choices.filter(isFirstLine).length > 0) {
                return;
            }
            const firstIsbnPair: IsbnNotValidatedPair = new IsbnNotValidatedPair()
            firstIsbnPair.dirtyChoices = line.words[0].choices;
            firstIsbnPair.cleanChoices = line.words[1].choices;
            const secondIsbnPair: IsbnNotValidatedPair = new IsbnNotValidatedPair()
            secondIsbnPair.dirtyChoices = line.words[2].choices;
            secondIsbnPair.cleanChoices = line.words[3].choices;
            isbnNotValidatedPairs.push(firstIsbnPair, secondIsbnPair);
            return;
        }
        if(line.words>0) {
            console.error('Problem in parsing line! (\''+line.text+'\')');
        }
    }

}



// WEBPACK FOOTER //
// ./src/app/+app-custom/utils/isbn-utils.ts